import React, { useState, useEffect, useMemo } from 'react';
import { useAbility } from '@casl/react';
import { useDispatch } from 'react-redux';
import { Accordion, Button, Col, Form, FormControl, Row } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { sendAlert } from '../../actions/utils';
import { ButtonTooltip, ComponentDataTable, DefaultModal, HRDashboardStyled, Icon, LinkBtn } from '../../components';
import { AbilityContext } from '../../config/abilityContext';
import { deleteCandidateRequest } from '../../requests/candidates';
import { showSelectionProcessRequest } from '../../requests/selectionProcesses';
import { camelCaseEmptyStringRecursive, filterArray } from '../../services/utils';
import basicSelectionProcess from './basicSelectionProcess';
import SelectionProcessInfo from './SelectionProcessInfo';
import SelectionProcessShowDetail from './SelectionProcessShowDetail';
import columns from './ShowColumns';

import './style.scss';

const SelectionProcessShow = ({ match }) => {
  const selectionProcessId = match.params.id;
  const ability = useAbility(AbilityContext);
  const history = useHistory();
  const [activeCard, setActiveCard] = useState('all');
  const [copySuccess, setCopySuccess] = useState('');
  const [customParams, setCustomParams] = useState(null);
  const [defaultModalBody, setDefaultModalBody] = useState('');
  const [defaultModalItemId, setDefaultModalItemId] = useState('');
  const [defaultModalShow, setDefaultModalShow] = useState(false);
  const [filterCandidates, setFilterCandidates] = useState([]);
  const [moreData, setMoreData] = useState(true);
  const [onRequest, setOnRequest] = useState(false);
  const [selectionProcess, setSelectionProcess] = useState(basicSelectionProcess);
  const [step, setStep] = useState(0);
  const [textInput, setTextInput] = useState('');
  const [accordionState, setAccordionState] = useState({
    resumen: true,
    candidate: true
  });
  const dispatch = useDispatch();

  const filters = useMemo(
    () => [
      'candidate.fullName',
      'candidate.nationalIdentification',
      'candidate.translatedCandidateType',
      'candidate.careers',
      'candidate.communeName',
      'selectionProcessStage.label'
    ],
    []
  );

  const fetchSelectionProcess = () => {
    setOnRequest(true);
    showSelectionProcessRequest(selectionProcessId, {
      dispatch,
      successCallback: response => setSelectionProcess(camelCaseEmptyStringRecursive(response.data)),
      callback: () => setOnRequest(false)
    });
  };

  const handleClick = card => {
    switch (card) {
      case 'applied':
        setCustomParams('applied');
        setActiveCard('applied');
        break;
      case 'accepted':
        setCustomParams('accepted');
        setActiveCard('accepted');
        break;
      case 'rejected':
        setCustomParams('rejected');
        setActiveCard('rejected');
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
    setMoreData(!moreData);
  };

  const handleDefaultModalClose = () => setDefaultModalShow(false);

  const handleDeleteRequest = () => {
    setOnRequest(true);
    deleteCandidateRequest(defaultModalItemId, {
      dispatch,
      successCallback: () => dispatch(sendAlert({ kind: 'success', message: 'Candidato eliminado con éxito' })),
      callback: () => {
        handleDefaultModalClose();
        setMoreData(!moreData);
        setOnRequest(false);
      }
    });
  };

  const handleButtonClick = (item, action) => {
    switch (action) {
      case 'show':
        history.push(`/selection_processes/${selectionProcess.id}/candidates/${item.candidate.id}`);
        break;
      case 'edit':
        history.push(`/selection_processes/${selectionProcess.id}/candidates/${item.candidate.id}/edit`);
        break;
      case 'destroy':
        setDefaultModalShow(true);
        setDefaultModalItemId(item.candidate.id);
        setDefaultModalBody(`¿Estás seguro que deseas eliminar a ${item.candidate.fullName}?`);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log('Error: Action not found');
    }
  };

  const subHeaderComponent = () => (
    <Form.Group className="ml-auto w-25">
      <FormControl
        placeholder="Buscar"
        name="textInput"
        value={textInput}
        onChange={e => setTextInput(e.target.value)}
      />
    </Form.Group>
  );

  const filteredArray = () => {
    let filtered = selectionProcess.selectionProcessCandidates;
    if (customParams) filtered = filtered.filter(item => item.status === customParams);
    setFilterCandidates(filterArray(filtered, textInput, filters));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(selectionProcess.publicUrl);
    setCopySuccess('Copiado');
  };

  const handleNewCandidate = () => {
    history.push(`/selection_processes/${selectionProcessId}/candidates/new`);
  };

  const statusAddsCandidates = status => ['active', 'generated', 'suspended'].includes(status);

  const toggleAccordion = key => {
    setAccordionState(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => window.scrollTo(0, 0), []);
  useEffect(fetchSelectionProcess, [moreData]);
  useEffect(filteredArray, [textInput, customParams, selectionProcess.selectionProcessCandidates]);

  return (
    <>
      <Row className="my-3">
        <Col xs="auto" className="mt-3">
          <LinkBtn variant="circle-dark" className="btn-circle" block to="/selection_processes_list">
            <Icon icon="chevron-back" />
          </LinkBtn>
        </Col>
        <Col xs={10} className="mt-3">
          <Row>
            <Col md={12}>
              <h2 className="mt-1">
                {selectionProcess.correlativeIdentification} - {selectionProcess.name}
              </h2>
            </Col>
          </Row>
        </Col>
      </Row>

      <div className="yellow-wrapper">
        <Row className="">
          <Col md={3} xs={12}>
            <HRDashboardStyled
              title={selectionProcess.appliedCandidates}
              subtitle2="Candidatos activos"
              TopHeaderColor="#fff"
              GeneralColor="#2ecc71"
              TypeCard="OnlyInformation"
              topHeight="15"
              CardName="Applied"
              onRequest={onRequest}
              onClick={() => handleClick('applied')}
            />
          </Col>
          <Col md={3} xs={12}>
            <HRDashboardStyled
              title={selectionProcess.acceptedCandidates}
              subtitle2="Candidatos aceptados"
              TopHeaderColor="#fff"
              GeneralColor="#f1c40f"
              TypeCard="OnlyInformation"
              topHeight="15"
              CardName="Accepted"
              onRequest={onRequest}
              onClick={() => handleClick('accepted')}
            />
          </Col>
          <Col md={3} xs={12}>
            <HRDashboardStyled
              title={selectionProcess.rejectedCandidates}
              subtitle2="Candidatos rechazados"
              TopHeaderColor="#fff"
              GeneralColor="#e74c3c"
              TypeCard="OnlyInformation"
              topHeight="15"
              CardName="Rejected"
              onRequest={onRequest}
              onClick={() => handleClick('rejected')}
            />
          </Col>
          <Col md={{ span: 2, offset: 1 }}>
            {ability.can('create', 'Candidate') && (
              <Button block variant="primary" onClick={handleNewCandidate}>
                Nuevo Candidato
              </Button>
            )}
          </Col>
        </Row>

        <Accordion className="my-4">
          <Col className="top-header-employee py-2">
            <Accordion.Toggle
              as={Col}
              eventKey="resumen"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => toggleAccordion('resumen')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Resumen del Proceso</span>
              <span className="h4 mr-2">{accordionState.resumen ? '−' : '+'}</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="resumen" in={accordionState.resumen}>
            <SelectionProcessInfo selectionProcess={selectionProcess} />
          </Accordion.Collapse>
        </Accordion>

        <Accordion className="my-4">
          <Col className="top-header-employee py-2">
            <Accordion.Toggle
              as={Col}
              eventKey="candidate"
              className="card-header-title d-flex justify-content-between align-items-center"
              onClick={() => toggleAccordion('candidate')}
              style={{ textAlign: 'right', cursor: 'pointer' }}
            >
              <span>Candidatos</span>
              <span className="h4 mr-2">{accordionState.candidate ? '−' : '+'}</span>
            </Accordion.Toggle>
          </Col>

          <Accordion.Collapse eventKey="candidate" in={accordionState.candidate}>
            <>
              {statusAddsCandidates(selectionProcess.status) && (
                <Row className="d-flex justify-content-between mt-3">
                  <Col md={6} lg={4} className="mb-3">
                    <Row>
                      <Col md={6} className="ml-2">
                        <Button block variant="info" onClick={copyToClipboard}>
                          Copiar Link Público
                        </Button>
                      </Col>
                      {copySuccess && (
                        <Col md={2} className="d-flex align-items-center justify-content-center my-1">
                          {copySuccess}
                        </Col>
                      )}
                    </Row>
                  </Col>
                  <Col md={6}>
                    <div className="float-right">
                      <ButtonTooltip
                        disabled={onRequest}
                        onClick={() => setStep(1)}
                        variant="circle-primary"
                        className={`btn-circle toogle ${step === 0 ? '' : 'active'} `}
                        text="Etapas"
                      >
                        <Icon icon="trello" />
                      </ButtonTooltip>
                    </div>
                    <div className="float-right">
                      <ButtonTooltip
                        onClick={() => setStep(0)}
                        variant="circle-primary"
                        className={`btn-circle toogle ${step === 0 ? 'active' : ''} `}
                        text="Candidatos"
                      >
                        <Icon icon="list-outline" />
                      </ButtonTooltip>
                    </div>
                  </Col>
                </Row>
              )}
              <Row className="mt-3 pb-0" />
              {activeCard !== 'all' && (
                <Col xs={12}>
                  <ButtonTooltip
                    onClick={() => {
                      setFilterCandidates(selectionProcess.selectionProcessCandidates);
                      setActiveCard('all');
                      setCustomParams(null);
                    }}
                    variant="link"
                    className="text-primary"
                    size="sm"
                    text="Volver"
                  >
                    <Icon icon="chevron-back" width={15} />
                    <span>Volver a tabla inicial</span>
                  </ButtonTooltip>
                </Col>
              )}
              {step === 0 ? (
                <div className="data-table-yellow px-2">
                  <ComponentDataTable
                    columns={columns(handleButtonClick)}
                    totalRows={filterCandidates?.length}
                    data={filterCandidates}
                    moreData={moreData}
                    onRequest={onRequest}
                    sortServer={false}
                    subHeaderComponent={subHeaderComponent()}
                    customParams={customParams}
                    onRowClicked={item => handleButtonClick(item, 'show')}
                    pointerOnHover
                  />
                </div>
              ) : (
                <SelectionProcessShowDetail
                  selectionProcess={selectionProcess}
                  statusFilter={customParams}
                  setMoreData={setMoreData}
                />
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </div>
      <DefaultModal
        title="Eliminar Candidato"
        body={defaultModalBody}
        show={defaultModalShow}
        handleClose={handleDefaultModalClose}
        handleConfirm={handleDeleteRequest}
        titleBtnClose="Cancelar"
        titleBtnSave="Confirmar"
      />
    </>
  );
};

export default SelectionProcessShow;
